import React from 'react';
import { useDispatch } from 'react-redux';

import { ModalEnum } from '../../ModalEnum';
import { flashMessageError, loginUserWithApple, loginUserWithFacebook, loginUserWithGoogle } from '../../store/session';
import T from '../Translate';

import AppleLoginButton from './AppleLoginButton';
import FacebookLoginButton from './FacebookLoginButton';
import GoogleLoginButton from './GoogleLoginButton';

import styles from './socialLogin.module.scss';

interface Props {
  disabled?: boolean;
  apple: string;
  facebook: string;
  google: string;

  track?(type: ModalEnum): void;

  onSuccess(): void;
}

export default function SocialLoginButtons(props: Props) {
  const dispatch = useDispatch();

  const onLoginError = (message?: string) => (err?: Error) => {
    dispatch(flashMessageError(err?.message ?? message));
  };

  const handleAppleError = (error?: Error) => {
    dispatch(flashMessageError(error?.message ?? 'flash.appleSignInError'));
  };

  const handleFacebookError = (error?: Error, blocked?: boolean) => {
    if (blocked || !window.FB) {
      dispatch(flashMessageError('flash.facebookSignInError.insufficientConsent'));
    } else {
      dispatch(flashMessageError(error?.message ?? 'flash.facebookSignInError'));
    }
  };

  const handleGoogleError = (error?: Error, loadingFailure = false) => {
    if (loadingFailure) {
      dispatch(flashMessageError('flash.googleSignInError.insufficientConsent'));
    } else {
      dispatch(flashMessageError(error?.message ?? 'flash.googleSignInError'));
    }
  };

  const handleAppleLogin = async (response: Apple.Authorization) => {
    try {
      if (!response.authorization?.id_token) {
        onLoginError('flash.appleSignInError')(null);
        return;
      }
      await dispatch(loginUserWithApple(response.authorization.id_token));
      props.track?.(ModalEnum.APPLE);
      props.onSuccess();
    } catch (error) {
      onLoginError('flash.appleSignInError')(error);
    }
  };

  const handleFacebookLogin = async ({ accessToken, error }) => {
    try {
      if (!accessToken) {
        handleFacebookError(error);
        return;
      }
      await dispatch(loginUserWithFacebook(accessToken));
      props.track?.(ModalEnum.FACEBOOK);
      props.onSuccess();
    } catch (error) {
      handleFacebookError(error);
    }
  };

  const handleGoogleLogin = async (access_token: string) => {
    try {
      if (!access_token) {
        handleGoogleError(null);
        return;
      }
      await dispatch(loginUserWithGoogle(access_token));
      props.track?.(ModalEnum.GOOGLE);
      props.onSuccess();
    } catch (error) {
      handleGoogleError(error);
    }
  };

  return (
    <div className={styles.container}>
      <AppleLoginButton
        clientId={process.env.GATSBY_APPLE_CLIENT_ID}
        onFailure={handleAppleError}
        onSuccess={handleAppleLogin}
      >
        <T id={props.apple} />
      </AppleLoginButton>

      <FacebookLoginButton
        appId={process.env.GATSBY_FACEBOOK_APP_ID}
        onFailure={handleFacebookError}
        onSuccess={handleFacebookLogin}
        version={process.env.GATSBY_FACEBOOK_SDK_VERSION}
      >
        <T id={props.facebook} />
      </FacebookLoginButton>

      <GoogleLoginButton
        clientId={process.env.GATSBY_GOOGLE_CLIENT_ID}
        onFailure={handleGoogleError}
        onSuccess={handleGoogleLogin}
      >
        <T id={props.google} />
      </GoogleLoginButton>
    </div>
  );
}
